import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../../core/hooks/useTranslation'
import Button from '../ui/Button'
import Badge from '../ui/Badge'

export interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  disabled?: boolean
}

interface UserSidebarProps {
  navigationItems: NavigationItem[]
  logoHref?: string
  variant?: 'dark' | 'light'
  showHelpSection?: boolean
  footerContent?: ReactNode
  className?: string
  isOpen?: boolean // For mobile responsive behavior
  onClose?: () => void // Close callback for mobile
}

export default function UserSidebar({
  navigationItems,
  logoHref,
  variant = 'dark',
  showHelpSection = true,
  footerContent,
  className = '',
  isOpen = true,
  onClose
}: UserSidebarProps) {
  const location = useLocation()
  const { isRTL } = useTranslation()

  const sidebarClasses = variant === 'dark' 
    ? `sidebar fixed top-0 w-64 h-screen overflow-y-auto z-50 md:z-30 transition-transform duration-300 ${
        isOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'
      } md:translate-x-0`
    : 'hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0'
  
  const navLinkClasses = variant === 'dark'
    ? 'nav-link'
    : 'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
  
  const activeNavLinkClasses = variant === 'dark'
    ? 'nav-link-active'
    : 'bg-blue-100 text-blue-900'

  return (
    <nav className={`${sidebarClasses} ${isRTL ? 'right-0' : 'left-0'} ${className}`}>
      {variant === 'dark' ? (
        <div className="p-4 pt-2 relative">
          {/* Logo area */}
          {logoHref && (
            <div className="mb-2 pb-2 border-b border-white/10">
              <Link to={logoHref} className="flex items-center group" onClick={() => onClose?.()}>
                <div className={`w-12 h-12 ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center rounded-2xl bg-white/10 border border-white/20 shadow-sm group-hover:scale-105 transition-transform`}>
                  <img
                    src="/assets/images/logo_white.png"
                    alt={isRTL ? 'شعار المنصة' : 'Platform Logo'}
                    className="w-9 h-9 object-contain"
                    loading="lazy"
                  />
                </div>
                <div>
                  <h1 className="text-base font-bold leading-tight text-white">
                    {isRTL ? 'هاتلى' : 'Hatli'}
                  </h1>
                  <p className={`${isRTL ? 'text-[9px]' : 'text-[7px]'} font-semibold uppercase tracking-[0.25em] text-white/60`}>
                    {isRTL ? 'منصة القوائم الرقمية' : 'Digital Menu Platform'}
                  </p>
                </div>
              </Link>
            </div>
          )}

          {/* Navigation Menu */}
          <div className="space-y-1">
            <h2 className="text-[9px] font-semibold text-white/60 uppercase tracking-wider mb-2">
              {isRTL ? 'القائمة الرئيسية' : 'Main Menu'}
            </h2>
            
            {navigationItems.map((item, index) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href)
              const Icon = item.icon
              
              return (
                <div key={item.name} className={`animate-slide-in-right animate-delay-${index * 75}`}>
                  {item.disabled ? (
                    <div className="nav-link text-secondary-300 cursor-not-allowed">
                      <Icon className={`w-4 h-4 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                      <span className={`flex-1 whitespace-nowrap overflow-hidden text-ellipsis ${isRTL ? 'text-[13px]' : 'text-[13px]'}`}>{item.name}</span>
                      <Badge variant="gray" size="sm" className="flex-shrink-0">
                        {isRTL ? 'قريباً' : 'Soon'}
                      </Badge>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`${navLinkClasses} ${isActive ? activeNavLinkClasses : ''}`}
                      onClick={() => onClose?.()}
                    >
                      <Icon className={`w-4 h-4 ${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0`} />
                      <span className={`flex-1 whitespace-nowrap overflow-hidden text-ellipsis ${isRTL ? 'text-[13px]' : 'text-xs'}`}>{item.name}</span>
                      {isActive && variant === 'dark' && (
                        <div className="w-2 h-2 bg-[#F97316] rounded-full animate-pulse flex-shrink-0" />
                      )}
                    </Link>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            {logoHref ? (
              <div className="flex items-center flex-shrink-0 px-4">
                <Link to={logoHref}>
                  <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                </Link>
              </div>
            ) : (
              <div className="flex items-center flex-shrink-0 px-4">
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
              </div>
            )}
            <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href || location.pathname.startsWith(item.href)
                const Icon = item.icon
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${navLinkClasses} ${
                      isActive
                        ? activeNavLinkClasses
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon
                      className={`${isRTL ? 'ml-3' : 'mr-3'} flex-shrink-0 h-6 w-6 ${
                        isActive ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    {item.name}
                  </Link>
                )
              })}
            </nav>
          </div>
          {footerContent && (
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              {footerContent}
            </div>
          )}
        </div>
      )}

      {/* Help Section - Only for dark variant */}
      {variant === 'dark' && showHelpSection && (
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="p-4 rounded-2xl border border-white/10 bg-white/5 text-white/90">
            <h3 className="text-xs font-semibold text-white mb-2">
              {isRTL ? 'تحتاج مساعدة؟' : 'Need Help?'}
            </h3>
            <p className="text-[11px] text-white/70 mb-3 leading-relaxed">
              {isRTL ? 'تواصل مع فريق الدعم' : 'Contact our support team'}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full border-[#F97316] text-[#F97316] hover:bg-[#F97316]/20 hover:text-white"
            >
              {isRTL ? 'الدعم الفني' : 'Support'}
            </Button>
          </div>
        </div>
      )}
    </nav>
  )
}

